// src/components/admin/AdminMenu.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Edit2, Trash2, Save, X, Search,
  Utensils, Tag, Settings, DollarSign,
  ChevronDown, ChevronUp, Eye, EyeOff, MoreVertical, Package,
  Image as ImageIcon, ChevronRight,
  Download, Upload, Loader2,
  Filter
} from 'lucide-react';
import * as menuService from '../../services/menuService';
import { supabase } from '../../services/supabase';
import * as XLSX from 'xlsx';

const AdminMenuStyles = () => (
  <style>{`
    .admin-menu-container {
      max-width: 100%;
      margin: 0 auto;
    }

    /* 標籤頁切換 */
    .menu-tabs {
      display: flex;
      gap: 8px;
      margin-bottom: 24px;
      border-bottom: 2px solid #e2e8f0;
      padding-bottom: 0;
      overflow-x: auto;
    }
    .menu-tab {
      padding: 12px 24px;
      background: transparent;
      border: none;
      border-bottom: 3px solid transparent;
      color: #64748b;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: -2px;
      white-space: nowrap;
    }
    .menu-tab:hover {
      color: #1e293b;
    }
    .menu-tab.active {
      color: #1e293b;
      font-weight: 600;
      border-bottom-color: #1e293b;
    }

    /* 工具列 */
    .menu-toolbar {
      background: white;
      padding: 16px 20px;
      border-radius: 12px;
      margin-bottom: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      gap: 16px;
      flex-wrap: wrap;
    }
    
    .toolbar-left {
      display: flex;
      gap: 16px;
      flex: 1;
      align-items: center;
      flex-wrap: wrap;
    }

    .search-box {
      display: flex;
      align-items: center;
      gap: 10px;
      background: white;
      padding: 8px 16px;
      border-radius: 8px;
      border: 1px solid var(--saas-border-color);
      flex: 1;
      min-width: 200px;
      max-width: 400px;
    }
    .search-box input {
      border: none;
      background: transparent;
      outline: none;
      width: 100%;
      font-size: 0.95rem;
    }

    /* Filter 下拉選單樣式 */
    .filter-box {
      display: flex;
      align-items: center;
      gap: 10px;
      background: white;
      padding: 8px 16px;
      border-radius: 8px;
      border: 1px solid var(--saas-border-color);
      min-width: 180px;
      position: relative;
    }
    .filter-select {
      border: none;
      background: transparent;
      outline: none;
      width: 100%;
      font-size: 0.95rem;
      color: #334155;
      cursor: pointer;
      appearance: none; /* 移除預設箭頭 */
    }

    /* 動作按鈕區塊 */
    .action-buttons {
      display: flex;
      gap: 8px;
      align-items: center;
    }
    
    /* 隱藏檔案輸入框 */
    .file-input {
      display: none;
    }

    /* 按鈕樣式 */
    .btn-primary {
      background: #2F7D5E;
      color: white;
      padding: 10px 20px;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.2s;
    }
    .btn-primary:hover {
      background: #245F48;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
    }
    .btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }

    .btn-secondary {
      background: white;
      color: #475569;
      border: 1px solid var(--saas-border-color);
      padding: 8px 16px;
      border: none;
      border-radius: 6px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .btn-secondary:hover {
      background: var(--saas-primary-light);
    }

    .btn-danger {
      background: #ef4444;
      color: white;
      padding: 8px 16px;
      border: none;
      border-radius: 6px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
    }
    .btn-danger:hover {
      background: #dc2626;
    }

    .btn-icon {
      background: transparent;
      border: none;
      color: #64748b;
      cursor: pointer;
      padding: 6px;
      border-radius: 6px;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .btn-icon:hover {
      background: var(--saas-primary-light);
      color: #2F7D5E;
    }

    .btn-small {
      padding: 4px 12px;
      font-size: 0.85rem;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    /* 表格容器 */
    .table-container {
      background: white;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      overflow: hidden;
    }

    /* 表格樣式 */
    .data-table {
      width: 100%;
      border-collapse: collapse;
    }

    .data-table thead {
      background: white;
      border-bottom: 2px solid #e2e8f0;
    }

    .data-table th {
      padding: 14px 16px;
      text-align: left;
      font-size: 0.85rem;
      font-weight: 700;
      color: #475569;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .data-table tbody tr {
      border-bottom: 1px solid #f1f5f9;
      transition: background 0.15s;
    }

    .data-table tbody tr:hover {
      background: var(--saas-primary-light);
    }

    .data-table td {
      padding: 14px 16px;
      font-size: 0.95rem;
      color: #334155;
    }

    .table-cell-name {
      font-weight: 600;
      color: #1e293b;
    }

    .table-cell-subtitle {
      font-size: 0.85rem;
      color: #64748b;
      margin-top: 2px;
    }

    .table-actions {
      display: flex;
      gap: 4px;
      justify-content: flex-end;
    }

    /* 圖片欄位 */
    .table-image {
      width: 60px;
      height: 60px;
      border-radius: 8px;
      object-fit: cover;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #94a3b8;
      font-size: 0.75rem;
      border: 2px solid #e2e8f0;
    }

    .table-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 6px;
    }

    .table-image-placeholder {
      width: 60px;
      height: 60px;
      border-radius: 8px;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #94a3b8;
      font-size: 0.75rem;
      border: 2px dashed #cbd5e1;
    }

    /* 可展開行 */
    .expandable-row {
      cursor: pointer;
    }

    .expand-icon {
      transition: transform 0.2s;
      display: inline-flex;
      vertical-align: middle;
    }

    .expand-icon.expanded {
      transform: rotate(90deg);
    }

    .expanded-content-row td {
      padding: 0 !important;
      background: white;
    }

    .expanded-content {
      padding: 20px;
      border-top: 1px solid #e2e8f0;
    }

    .modifier-options-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 12px;
    }

    .modifier-option-card {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .modifier-option-info {
      flex: 1;
    }

    .modifier-option-name-text {
      font-weight: 600;
      color: #1e293b;
      font-size: 0.95rem;
    }

    .modifier-option-alternate {
      font-size: 0.85rem;
      color: #64748b;
      margin-top: 2px;
    }

    /* 狀態徽章 */
    .status-badge {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 6px;
      font-size: 0.8rem;
      font-weight: 600;
    }
    .status-active {
      background: #dcfce7;
      color: #166534;
    }
    .status-inactive {
      background: #fee2e2;
      color: #991b1b;
    }
    .status-hidden {
      background: white;
      color: #6b7280;
      border: 1px solid #e2e8f0;
    }

    /* 有貨/沒貨切換按鈕 */
    .availability-toggle {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 4px 12px 4px 6px;
      border-radius: 20px;
      border: none;
      cursor: pointer;
      font-size: 0.8rem;
      font-weight: 600;
      transition: all 0.2s;
      min-width: 80px;
    }
    .availability-toggle .toggle-slider {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: white;
      box-shadow: 0 1px 2px rgba(0,0,0,0.15);
      transition: all 0.2s;
    }
    .availability-toggle.available {
      background: #2F7D5E;
      color: white;
    }
    .availability-toggle.soldout {
      background: #ef4444;
      color: white;
    }
    .availability-toggle:hover {
      opacity: 0.9;
      transform: scale(1.02);
    }
    .availability-toggle:active {
      transform: scale(0.98);
    }

    /* 手機版 Toggle 開關 */
    .mobile-availability-toggle {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      background: transparent;
      transition: all 0.2s;
    }
    .mobile-availability-toggle .toggle-track {
      width: 36px;
      height: 20px;
      border-radius: 10px;
      position: relative;
      transition: all 0.2s;
    }
    .mobile-availability-toggle.available .toggle-track {
      background: #2F7D5E;
    }
    .mobile-availability-toggle.soldout .toggle-track {
      background: #ef4444;
    }
    .mobile-availability-toggle .toggle-thumb {
      position: absolute;
      top: 2px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      transition: all 0.2s;
    }
    .mobile-availability-toggle.available .toggle-thumb {
      left: 18px;
    }
    .mobile-availability-toggle.soldout .toggle-thumb {
      left: 2px;
    }
    .mobile-availability-toggle .toggle-label {
      font-size: 0.8rem;
      font-weight: 600;
      white-space: nowrap;
    }
    .mobile-availability-toggle.available .toggle-label {
      color: #2F7D5E;
    }
    .mobile-availability-toggle.soldout .toggle-label {
      color: #ef4444;
    }
    .mobile-availability-toggle:active {
      transform: scale(0.98);
    }

    .price-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      background: #dcfce7;
      color: #166534;
      padding: 4px 10px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 0.9rem;
    }

    .price-badge.discounted {
      background: #fef3c7;
      color: #92400e;
    }

    .tag-list {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }
    .tag-item {
      background: #eff6ff;
      color: #1e40af;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 500;
    }

    /* 多選標籤樣式 (給 Category 用) */
    .multi-select-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 12px;
      background: white;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      min-height: 80px;
    }

    .selectable-tag {
      padding: 6px 14px;
      border: 2px solid #e2e8f0;
      border-radius: 6px;
      background: white;
      color: #64748b;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      user-select: none;
    }

    .selectable-tag:hover {
      border-color: #2F7D5E;
      color: #2F7D5E;
      background: #eff6ff;
    }

    .selectable-tag.selected {
      background: #2F7D5E;
      color: white;
      border-color: #2F7D5E;
    }

    .selectable-tag.selected:hover {
      background: #245F48;
      border-color: #245F48;
    }
    
    /* Modifier 選擇器樣式 */
    .modifier-selector-container {
      background: white;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
    }
    
    .selected-modifiers-area {
      padding: 12px;
      background: #f0f9ff;
      border-bottom: 1px solid #e2e8f0;
      min-height: 60px;
    }
    
    .selected-modifier-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    
    .modifier-chip {
      display: flex;
      align-items: center;
      gap: 6px;
      background: #2F7D5E;
      color: white;
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 500;
    }
    
    .modifier-chip button {
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      opacity: 0.8;
    }
    .modifier-chip button:hover {
      opacity: 1;
    }

    .available-modifiers-area {
      padding: 12px;
    }

    .modifier-search-input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      margin-bottom: 12px;
      font-size: 0.9rem;
    }

    .available-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 8px;
      max-height: 200px;
      overflow-y: auto;
    }

    .available-item {
      padding: 8px 12px;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    
    .available-item:hover {
      background: var(--saas-primary-light);
      border-color: #94a3b8;
    }

    .available-item-name {
      font-size: 0.9rem;
      font-weight: 500;
      color: #334155;
    }
    
    .section-label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #64748b;
      margin-bottom: 8px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    /* Modal 樣式 */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
      animation: fadeIn 0.2s ease;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .modal-content {
      background: white;
      border-radius: 16px;
      width: 100%;
      max-width: 900px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      animation: slideUp 0.3s ease;
    }

    @keyframes slideUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    .modal-header {
      padding: 24px;
      border-bottom: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: sticky;
      top: 0;
      background: white;
      z-index: 10;
    }
    .modal-header h2 {
      font-size: 1.5rem;
      font-weight: 700;
      color: #1e293b;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .modal-body {
      padding: 24px;
    }

    .modal-footer {
      padding: 20px 24px;
      border-top: 1px solid #e2e8f0;
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      position: sticky;
      bottom: 0;
      background: white;
    }

    /* Form 樣式 */
    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }

    .form-group {
      margin-bottom: 20px;
    }
    .form-group.full-width {
      grid-column: 1 / -1;
    }
    .form-label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
      color: #334155;
      font-size: 0.9rem;
    }
    .form-label.required::after {
      content: '*';
      color: #ef4444;
      margin-left: 2px;
    }

    .form-input,
    .form-textarea,
    .form-select {
      width: 100%;
      padding: 10px 14px;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      font-size: 0.95rem;
      transition: all 0.2s;
      font-family: inherit;
    }
    .form-input:focus,
    .form-textarea:focus,
    .form-select:focus {
      outline: none;
      border-color: #2F7D5E;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
    .form-textarea {
      resize: vertical;
      min-height: 80px;
    }

    .form-checkbox {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
    }
    .form-checkbox input {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    .form-hint {
      font-size: 0.85rem;
      color: #64748b;
      margin-top: 4px;
    }

    .form-section {
      background: white;
      padding: 20px;
      border: 1px solid var(--saas-border-color);
      border-radius: 10px;
      margin-bottom: 24px;
    }
    .form-section-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #1e293b;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .modifier-group-card {
      border: 2px solid #e2e8f0;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 16px;
      background: white;
    }
    .modifier-group-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e2e8f0;
    }
    .modifier-group-title {
      font-weight: 600;
      color: #1e293b;
      font-size: 1rem;
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      color: #64748b;
      background: white;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    .empty-state-icon {
      width: 80px;
      height: 80px;
      margin: 0 auto 16px;
      opacity: 0.3;
    }

    /* 圖片上傳區域 */
    .image-upload-container {
      display: flex;
      gap: 12px;
      align-items: flex-start;
    }
    .image-upload-right {
      flex: 1;
      min-width: 0;
    }
    .image-upload-input-row {
      display: flex;
      gap: 8px;
    }

    /* 手機卡片列表 */
    .mobile-cards {
      display: none;
    }
    .mobile-card {
      background: white;
      border-radius: 12px;
      padding: 14px;
      margin-bottom: 12px;
      border: 1px solid #e2e8f0;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    }
    .mobile-card-header {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      margin-bottom: 10px;
    }
    .mobile-card-image {
      width: 60px;
      height: 60px;
      border-radius: 8px;
      overflow: hidden;
      flex-shrink: 0;
      background: #f1f5f9;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .mobile-card-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .mobile-card-info {
      flex: 1;
      min-width: 0;
    }
    .mobile-card-title {
      font-weight: 600;
      font-size: 0.95rem;
      color: #1e293b;
      margin-bottom: 4px;
    }
    .mobile-card-subtitle {
      font-size: 0.8rem;
      color: #64748b;
    }
    .mobile-card-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 10px;
      align-items: center;
    }
    .mobile-card-tag {
      font-size: 0.75rem;
      padding: 4px 10px;
      background: #f1f5f9;
      border-radius: 6px;
      color: #475569;
      font-weight: 500;
      line-height: 1.2;
      white-space: nowrap;
    }
    .mobile-card-tag.tag-category {
      background: #f1f5f9;
      color: #475569;
    }
    .mobile-card-tag.tag-modifier {
      background: #e8f5f0;
      color: #2F7D5E;
    }
    .mobile-card-tag.tag-active {
      background: #dcfce7;
      color: #166534;
    }
    .mobile-card-tag.tag-inactive {
      background: #fee2e2;
      color: #dc2626;
    }
    .mobile-card-tag.tag-gst {
      background: #e0f2fe;
      color: #0369a1;
    }
    .mobile-card-tag.tag-pst {
      background: #fce7f3;
      color: #be185d;
    }
    .mobile-card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 10px;
      border-top: 1px solid #f1f5f9;
    }
    .mobile-card-price {
      font-weight: 700;
      font-size: 1.1rem;
      color: #2F7D5E;
    }
    .mobile-card-actions {
      display: flex;
      gap: 8px;
    }
    .mobile-card-actions button {
      padding: 8px 12px;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 0.8rem;
    }
    .mobile-card-edit {
      background: #e8f5f0;
      color: #2F7D5E;
    }
    .mobile-card-delete {
      background: #fee2e2;
      color: #dc2626;
    }

    /* 響應式設計 */
    @media (max-width: 1024px) {
      .table-container {
        overflow-x: auto;
      }
      .data-table {
        min-width: 900px;
      }
    }

    @media (max-width: 768px) {
      /* 隱藏表格，顯示卡片 */
      .table-container {
        display: none;
      }
      .mobile-cards {
        display: block;
      }

      .menu-toolbar {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
        padding: 12px;
      }
      .toolbar-left {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
      }
      .search-box, .filter-box {
        max-width: 100%;
        min-width: auto;
      }
      .action-buttons {
        display: flex;
        gap: 8px;
        flex-wrap: nowrap;
      }
      .action-buttons .btn-secondary {
        padding: 10px 12px;
        min-width: auto;
      }
      .action-buttons .btn-primary {
        flex: 1;
        justify-content: center;
        padding: 10px 14px;
        font-size: 0.85rem;
      }
      .btn-primary, .btn-secondary {
        font-size: 0.85rem;
      }
      .form-row {
        grid-template-columns: 1fr;
      }

      /* Modal 手機適配 */
      .modal-overlay {
        padding: 0;
        align-items: flex-end;
      }
      .modal-content {
        max-width: 100%;
        max-height: 95vh;
        margin: 0;
        border-radius: 20px 20px 0 0;
        padding: 16px;
      }
      .modal-header {
        padding: 12px 0;
        margin-bottom: 12px;
      }
      .modal-header h2 {
        font-size: 1.1rem;
      }

      /* Form 手機適配 */
      .form-section {
        padding: 14px;
        margin-bottom: 14px;
      }
      .form-section-title {
        font-size: 0.95rem;
        margin-bottom: 14px;
      }
      .form-group {
        margin-bottom: 14px;
      }
      .form-label {
        font-size: 0.85rem;
        margin-bottom: 6px;
      }
      .form-input, .form-textarea, .form-select {
        padding: 10px 12px;
        font-size: 16px; /* 防止 iOS 縮放 */
      }
      .form-hint {
        font-size: 0.75rem;
      }

      /* Tabs 手機適配 */
      .menu-tabs {
        gap: 4px;
        padding: 0 12px;
        margin-bottom: 16px;
      }
      .menu-tab {
        padding: 10px 14px;
        font-size: 0.85rem;
      }

      /* 修飾項組手機適配 */
      .modifier-group-item {
        padding: 12px;
      }
      .modifier-option {
        padding: 10px;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }
      .modifier-option-info {
        width: 100%;
      }
      .modifier-option-actions {
        width: 100%;
        justify-content: flex-end;
      }

      /* 圖片上傳手機適配 */
      .image-upload-container {
        flex-direction: column;
      }
      .image-upload-input-row {
        flex-direction: column;
      }
      .image-upload-input-row button {
        width: 100%;
      }

      .hidden-mobile {
        display: none;
      }
    }

    @media (max-width: 480px) {
      .menu-tabs {
        overflow-x: auto;
        flex-wrap: nowrap;
        -webkit-overflow-scrolling: touch;
      }
      .menu-tab {
        white-space: nowrap;
        flex-shrink: 0;
        padding: 8px 12px;
        font-size: 0.8rem;
      }
      .modal-content {
        padding: 12px;
      }
      .form-section {
        padding: 12px;
      }
      .action-buttons {
        gap: 6px;
      }
      .action-buttons .btn-secondary {
        padding: 8px 10px;
      }
      .action-buttons .btn-primary {
        padding: 8px 12px;
        font-size: 0.8rem;
      }
    }
  `}</style>
);

const AdminMenu = () => {
  const [activeTab, setActiveTab] = useState('items'); // items, categories, modifiers
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Modal 內的修飾項搜尋狀態
  const [modifierSearchTerm, setModifierSearchTerm] = useState('');

  // 真實資料狀態
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [modifierGroups, setModifierGroups] = useState([]);

  // 初始載入資料
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'categories') {
        const result = await menuService.getCategories();
        if (result.success) {
          setCategories(result.data || []);
        } else {
          alert('載入分類失敗: ' + result.error);
        }
      } else if (activeTab === 'items') {
        const result = await menuService.getMenuItems();
        if (result.success) {
          setMenuItems(result.data || []);
        } else {
          alert('載入菜式失敗: ' + result.error);
        }
      } else if (activeTab === 'modifiers') {
        const result = await menuService.getModifierGroups();
        if (result.success) {
          // 載入每個組的詳細選項
          const groupsWithOptions = await Promise.all(
            result.data.map(async (group) => {
              const optionsResult = await menuService.getModifiersByGroupId(group.id);
              return {
                ...group,
                modifiers: optionsResult.success ? optionsResult.data : []
              };
            })
          );
          setModifierGroups(groupsWithOptions);
        } else {
          alert('載入修飾項組失敗: ' + result.error);
        }
      }
    } catch (error) {
      alert('載入資料時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  // 同時載入分類和修飾項組（用於表單和匯入匯出）
  useEffect(() => {
    const loadAllData = async () => {
      // 載入分類
      const catResult = await menuService.getCategories();
      if (catResult.success) {
        setCategories(catResult.data || []);
      }
      
      // 載入修飾項組
      const modResult = await menuService.getModifierGroups();
      if (modResult.success) {
          const groupsWithOptions = await Promise.all(
          (modResult.data || []).map(async (group) => {
            const optionsResult = await menuService.getModifiersByGroupId(group.id);
            return {
              ...group,
              modifiers: optionsResult.success ? optionsResult.data : []
            };
          })
        );
        setModifierGroups(groupsWithOptions);
      }
    };
    loadAllData();
  }, [activeTab]);

  // 快速切換菜式有貨/沒貨狀態
  const handleToggleAvailability = async (item) => {
    const newAvailability = !item.is_available;

    // 先在UI上更新（樂觀更新）
    setMenuItems(prev => prev.map(i =>
      i.id === item.id ? { ...i, is_available: newAvailability } : i
    ));

    try {
      const result = await menuService.updateMenuItem(item.id, {
        is_available: newAvailability
      });

      if (!result.success) {
        // 如果更新失敗，恢復原狀態
        setMenuItems(prev => prev.map(i =>
          i.id === item.id ? { ...i, is_available: !newAvailability } : i
        ));
        alert('更新失敗: ' + result.error);
      }
    } catch (error) {
      // 發生錯誤，恢復原狀態
      setMenuItems(prev => prev.map(i =>
        i.id === item.id ? { ...i, is_available: !newAvailability } : i
      ));
      alert('更新失敗: ' + error.message);
    }
  };

  const toggleExpandRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const [formData, setFormData] = useState({
    name: '',
    printer_name: '',
    slug: '',
    excerpt: '',
    description: '',
    description_en: '',
    fee_in_cents: 0,
    discounted_fee_in_cents: null,
    non_member_fee_in_cents: 0,
    tax_rates: 0,
    // === 修改處：新增 GST/PST 狀態 ===
    tax_gst: true,
    tax_pst: true,
    // =============================
    image_url: '',
    categories: [],
    modifier_groups: [],
    tags: [],
    allergens: [],
    is_available: true,
    is_hidden: false,
    is_complimentary: false,
    is_partysize: false,
    alternate_name: '',
    order: 1,
    schedule_ahead_days: 1,
    available_time: '',
    available_day: '',
    min_required: 0,
    max_allowed: 1,
    modifiers: []
  });

  // Image upload state and function
  const [isUploading, setIsUploading] = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { current, total }
  const fileInputRef = useRef(null);

  // Compress image client-side before upload (max 800px, WebP, quality 0.8)
  const compressImage = (file, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve) => {
      // GIF: skip compression to preserve animation
      if (file.type === 'image/gif') {
        resolve(file);
        return;
      }
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => resolve(blob ? new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' }) : file),
          'image/webp',
          quality
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file); // fallback to original
      };
      img.src = url;
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      alert('請選擇有效的圖片格式 (JPG, PNG, WebP, GIF)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('圖片大小不能超過 5MB');
      return;
    }

    setIsUploading(true);
    try {
      // Compress image before upload (resize to max 800px width, convert to WebP)
      const compressed = await compressImage(file);

      // Generate unique filename (always .webp after compression, unless GIF)
      const ext = compressed.type === 'image/gif' ? 'gif' : 'webp';
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${ext}`;
      const filePath = `${fileName}`;

      // Upload to Supabase Storage (24h cache for better mobile performance)
      const { data, error } = await supabase.storage
        .from('menu-images')
        .upload(filePath, compressed, {
          cacheControl: '86400',
          upsert: false,
          contentType: compressed.type,
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('menu-images')
        .getPublicUrl(filePath);

      // Update form data with the new URL
      setFormData(prev => ({ ...prev, image_url: publicUrl }));
    } catch (error) {
      alert('上傳失敗: ' + error.message);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatModifiersToSimpleString = (itemModifierGroupIds) => {
    if (!itemModifierGroupIds || itemModifierGroupIds.length === 0) return '';
    return itemModifierGroupIds
      .map(groupId => {
        const group = modifierGroups.find(g => g.id === groupId);
        return group ? (group.alternate_name || group.name) : null;
      })
      .filter(Boolean)
      .join(', ');
  };

  const handleExport = () => {
    let dataToExport = [];
    let fileName = '';

    if (activeTab === 'items') {
      fileName = 'menu_items_export';
      // 先 map 出含分類名的資料，再按分類名排序
      const itemsWithCat = menuItems.map(item => {
        const catNames = (item.categories || [])
          .map(id => categories.find(c => c.id === id))
          .filter(Boolean)
          .map(c => c.alternate_name || c.name)
          .join(', ');
        return { item, catNames };
      });
      itemsWithCat.sort((a, b) => a.catNames.localeCompare(b.catNames, 'zh-Hant'));
      dataToExport = itemsWithCat.map(({ item, catNames }) => {

        return {
          ID: item.id,
          ChineseName: item.alternate_name || '',
          EnglishName: item.name || '',
          PrinterName: item.printer_name || '',
          Category: catNames,
          Price: item.fee_in_cents / 100,
          GST: item.tax_gst ? 'Y' : 'N',
          PST: item.tax_pst ? 'Y' : 'N',
          Description: item.description || '',
          DescriptionEN: item.description_en || '',
          ImageURL: item.image_url || '',
          Available: item.is_available ? 'Y' : 'N',
          ModifierGroups: formatModifiersToSimpleString(item.modifier_groups)
        };
      });
    } else if (activeTab === 'categories') {
      fileName = 'categories_export';
      dataToExport = categories.map(c => ({
        ID: c.id,
        Name: c.alternate_name || c.name,
        EngName: c.name,
        Order: c.order,
        Excerpt: c.excerpt
      }));
    } else {
        alert('此頁面暫不支援匯出');
        return;
    }

    if (dataToExport.length === 0) {
      alert('沒有資料可匯出');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        setLoading(true);
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        if (activeTab === 'items') {
          await processImportedItems(data);
        } else {
          alert('目前僅支援菜式(Items)的批量匯入');
        }
      } catch (error) {
        alert('匯入失敗，請檢查檔案格式');
      } finally {
        setLoading(false);
        e.target.value = ''; 
      }
    };
    reader.readAsBinaryString(file);
  };

  const processImportedItems = async (rows) => {
    let successCount = 0;
    const currentCategories = [...categories];
    const currentModifierGroups = [...modifierGroups];
    setImportProgress({ current: 0, total: rows.length });

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      setImportProgress({ current: i + 1, total: rows.length });
      try {
        const rowCatNames = row['Category'] ? String(row['Category']).split(',').map(s => s.trim()) : [];
        const catIds = [];
        
        for (let catName of rowCatNames) {
          let cat = currentCategories.find(c => 
            (c.alternate_name === catName) || (c.name === catName)
          );
          if (cat) catIds.push(cat.id);
        }

        const modifierGroupIds = [];
        if (row['ModifierGroups']) {
          const groupNames = String(row['ModifierGroups']).split(',').map(s => s.trim()).filter(Boolean);
          
          for (let groupName of groupNames) {
            let group = currentModifierGroups.find(g => 
              (g.alternate_name === groupName) || (g.name === groupName)
            );
            
            if (group) {
              modifierGroupIds.push(group.id);
            } else {
              const newGroupRes = await menuService.createModifierGroup({
                name: groupName, 
                alternate_name: groupName,
                min_required: 0, 
                max_allowed: 1   
              });
              
              if (newGroupRes.success) {
                modifierGroupIds.push(newGroupRes.data.id);
                currentModifierGroups.push(newGroupRes.data); 
              }
            }
          }
        }

        const itemData = {
            name: row['EnglishName'] || row['Name'] || '',
            alternate_name: row['ChineseName'] || '',
            printer_name: row['PrinterName'],
            slug: row['ID'] ? undefined : generateUniqueSlug(row['EnglishName'] || row['Name']),
            fee_in_cents: Math.round((row['Price'] || 0) * 100),
            tax_gst: row['GST'] === 'Y',
            tax_pst: row['PST'] === 'Y',
            description: row['Description'] || '',
            description_en: row['DescriptionEN'] || '',
            image_url: row['ImageURL'],
            categories: catIds,
            modifier_groups: modifierGroupIds,
            is_available: row['Available'] === 'Y'
          };

        if (row['ID']) {
          await menuService.updateMenuItem(row['ID'], itemData);
        } else {
          await menuService.createMenuItem(itemData);
        }
        successCount++;
        
      } catch (err) {
        // silently ignore row import error
      }
    }
    
    setImportProgress(null);
    alert(`匯入完成！成功處理 ${successCount} / ${rows.length} 筆資料。`);
    loadData();
  };

  const generateUniqueSlug = (name) => {
    const baseSlug = name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-') 
      .replace(/^-+|-+$/g, '');
    
    const randomSuffix = Math.random().toString(36).substring(2, 7);
    return `${baseSlug}-${randomSuffix}`;
  };

  const handleOpenModal = (item = null) => {
    setModifierSearchTerm(''); 
    if (item) {
      setEditingItem(item);
      
      if (activeTab === 'items') {
        setFormData({
            ...item,
            // === 修改處：編輯時讀取 GST/PST，若無則預設為 true ===
            tax_gst: item.tax_gst !== false,
            tax_pst: item.tax_pst !== false,
        });
      } else {
        setFormData({ ...item });
      }
    } else {
      setEditingItem(null);
      if (activeTab === 'items') {
        setFormData({
          name: '',
          printer_name: '',
          slug: '',
          excerpt: '',
          description: '',
          fee_in_cents: 0,
          discounted_fee_in_cents: null,
          non_member_fee_in_cents: 0,
          tax_rates: 0,
          // === 修改處：新增時預設開啟 GST/PST ===
          tax_gst: true,
          tax_pst: true,
          // ==================================
          image_url: '',
          categories: [],
          modifier_groups: [],
          tags: [],
          allergens: [],
          is_available: true,
          is_hidden: false,
          is_complimentary: false,
          is_partysize: false,
        });
      } else if (activeTab === 'categories') {
        setFormData({
          name: '',
          alternate_name: '',
          excerpt: '',
          image_url: '',
          tags: [],
          order: 1,
          is_hidden: false,
          is_complimentary: false,
          schedule_ahead_days: 1,
          available_time: '',
          available_day: '',
        });
      } else if (activeTab === 'modifiers') {
        setFormData({
          name: '',
          alternate_name: '',
          printer_name: '',
          min_required: 0,
          max_allowed: 1,
          order: modifierGroups.length + 1,
          modifiers: []
        });
      }
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    if (activeTab === 'items') {
      if (!formData.alternate_name && !formData.name) {
        alert('請至少填寫中文名稱或英文名稱');
        return;
      }
    } else if (activeTab === 'categories' || activeTab === 'modifiers') {
      if (!formData.alternate_name && !formData.name) {
        alert('請至少填寫中文名稱或英文名稱');
        return;
      }
    }

    setLoading(true);
    
    try {
      let result;
      
      if (activeTab === 'categories') {
        const categoryData = {
          name: formData.name || formData.alternate_name,
          alternate_name: formData.alternate_name,
          excerpt: formData.excerpt,
          image_url: formData.image_url,
          tags: formData.tags,
          order: formData.order,
          is_hidden: formData.is_hidden,
          is_complimentary: formData.is_complimentary,
          schedule_ahead_days: formData.schedule_ahead_days,
          available_time: formData.available_time,
          available_day: formData.available_day,
          category_pair: formData.category_pair,
          min_spent_fee_in_cents: formData.min_spent_fee_in_cents,
        };

        if (editingItem) {
          result = await menuService.updateCategory(editingItem.id, categoryData);
        } else {
          result = await menuService.createCategory(categoryData);
        }
        
        if (result.success) {
          await loadData();
          handleCloseModal();
        } else {
          alert('儲存失敗: ' + result.error);
        }
      } else if (activeTab === 'items') {
        const itemData = {
          name: formData.name || formData.alternate_name,
          alternate_name: formData.alternate_name || '',
          printer_name: formData.printer_name,
          slug: formData.slug,
          excerpt: formData.excerpt,
          description: formData.description,
          description_en: formData.description_en || '',
          fee_in_cents: formData.fee_in_cents,
          discounted_fee_in_cents: formData.discounted_fee_in_cents,
          non_member_fee_in_cents: formData.non_member_fee_in_cents,
          tax_rates: formData.tax_rates,
          // === 修改處：儲存 GST/PST ===
          tax_gst: formData.tax_gst,
          tax_pst: formData.tax_pst,
          // =========================
          image_url: formData.image_url,
          categories: formData.categories,
          discounted_categories: formData.discounted_categories,
          modifier_groups: formData.modifier_groups,
          ingredients: formData.ingredients,
          tags: formData.tags,
          allergens: formData.allergens,
          variant_group: formData.variant_group,
          variants: formData.variants,
          options: formData.options,
          display_conditions: formData.display_conditions,
          redeem_points: formData.redeem_points,
          is_available: formData.is_available,
          is_hidden: formData.is_hidden,
          is_optional: formData.is_optional,
          is_complimentary: formData.is_complimentary,
          is_partysize: formData.is_partysize,
        };

        Object.keys(itemData).forEach(key => {
          if (itemData[key] === undefined) {
            delete itemData[key];
          }
        });
        
        // alternate_name 現已是 menu_items 的正式欄位

        if (editingItem) {
          result = await menuService.updateMenuItem(editingItem.id, itemData);
        } else {
          result = await menuService.createMenuItem(itemData);
        }
        
        if (result.success) {
          await loadData();
          handleCloseModal();
        } else {
          alert('儲存失敗: ' + result.error);
        }
      } else if (activeTab === 'modifiers') {
        const { modifiers, ...groupData } = formData;
        
        const modifierGroupData = {
          is_modifier_group: groupData.is_modifier_group,
          name: groupData.name || groupData.alternate_name,
          alternate_name: groupData.alternate_name,
          printer_name: groupData.printer_name,
          min_required: groupData.min_required,
          max_allowed: groupData.max_allowed,
          order: groupData.order || 1,
        };
        
        if (editingItem) {
          result = await menuService.updateModifierGroup(editingItem.id, modifierGroupData);
          
          if (result.success) {
            const oldModifiers = await menuService.getModifiersByGroupId(editingItem.id);
            if (oldModifiers.success && oldModifiers.data) {
              await Promise.all(
                oldModifiers.data.map(mod => menuService.deleteModifier(mod.id))
              );
            }
            
            if (modifiers && modifiers.length > 0) {
              const modifiersWithGroupId = modifiers.map(mod => ({
                modifier_group_id: editingItem.id,
                name: mod.name || mod.alternate_name,
                alternate_name: mod.alternate_name,
                printer_name: mod.printer_name,
                fee_in_cents: mod.fee_in_cents || 0,
                discounted_fee_in_cents: mod.discounted_fee_in_cents,
                non_member_fee_in_cents: mod.non_member_fee_in_cents || 0,
                tags: mod.tags || [],
                tax_rates: mod.tax_rates || 0,
                image_url: mod.image_url,
                is_available: mod.is_available !== false,
                is_complimentary: mod.is_complimentary || false,
                order: mod.order || 1,
              }));
              await menuService.bulkCreateModifiers(modifiersWithGroupId);
            }
            
            await loadData();
            handleCloseModal();
          } else {
            alert('儲存失敗: ' + result.error);
          }
        } else {
          result = await menuService.createModifierGroup(modifierGroupData);
          
          if (result.success) {
            if (modifiers && modifiers.length > 0) {
              const modifiersWithGroupId = modifiers.map(mod => ({
                modifier_group_id: result.data.id,
                name: mod.name || mod.alternate_name,
                alternate_name: mod.alternate_name,
                printer_name: mod.printer_name,
                fee_in_cents: mod.fee_in_cents || 0,
                discounted_fee_in_cents: mod.discounted_fee_in_cents,
                non_member_fee_in_cents: mod.non_member_fee_in_cents || 0,
                tags: mod.tags || [],
                tax_rates: mod.tax_rates || 0,
                image_url: mod.image_url,
                is_available: mod.is_available !== false,
                is_complimentary: mod.is_complimentary || false,
                order: mod.order || 1,
              }));
              await menuService.bulkCreateModifiers(modifiersWithGroupId);
            }
            
            await loadData();
            handleCloseModal();
          } else {
            alert('儲存失敗: ' + result.error);
          }
        }
      }
    } catch (error) {
      alert('儲存時發生錯誤: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('確定要刪除嗎？此操作無法復原。')) {
      return;
    }

    setLoading(true);
    try {
      let result;
      
      if (activeTab === 'categories') {
        result = await menuService.deleteCategory(id);
      } else if (activeTab === 'items') {
        result = await menuService.deleteMenuItem(id);
      } else if (activeTab === 'modifiers') {
        result = await menuService.deleteModifierGroup(id);
      }

      if (result.success) {
        await loadData();
      } else {
        alert('刪除失敗: ' + result.error);
      }
    } catch (error) {
      alert('刪除時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? (category.alternate_name || category.name) : '-';
  };

  if (loading && (
    (activeTab === 'categories' && categories.length === 0) ||
    (activeTab === 'items' && menuItems.length === 0) ||
    (activeTab === 'modifiers' && modifierGroups.length === 0)
  )) {
    return (
      <>
        <AdminMenuStyles />
        <div className="admin-menu-container">
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e2e8f0',
              borderTopColor: '#2F7D5E',
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{color: '#64748b'}}>載入中...</p>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  const filteredItems = () => {
    let items = activeTab === 'categories' ? categories : 
                 activeTab === 'items' ? menuItems : modifierGroups;
    
    // 1. 分類篩選 (僅在菜式管理且有選分類時生效)
    if (activeTab === 'items' && categoryFilter !== 'all') {
      items = items.filter(item => 
        item.categories && item.categories.includes(categoryFilter)
      );
    }

    // 2. 按分類排序 (菜式管理)
    if (activeTab === 'items' && categoryFilter === 'all') {
      const catOrder = {};
      categories.forEach((cat, idx) => { catOrder[cat.id] = idx; });
      items = [...items].sort((a, b) => {
        const aCat = (a.categories && a.categories[0]) ? (catOrder[a.categories[0]] ?? 999) : 999;
        const bCat = (b.categories && b.categories[0]) ? (catOrder[b.categories[0]] ?? 999) : 999;
        return aCat - bCat;
      });
    }

    // 3. 關鍵字搜尋
    if (!searchTerm) return items;
    
    return items.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      
      if (activeTab === 'items') {
        return (item.name && item.name.toLowerCase().includes(searchLower)) ||
               (item.alternate_name && item.alternate_name.toLowerCase().includes(searchLower));
      }
      
      return (item.name && item.name.toLowerCase().includes(searchLower)) ||
             (item.alternate_name && item.alternate_name.toLowerCase().includes(searchLower));
    });
  };

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const parsePriceToCents = (dollarAmount) => {
    const cleaned = String(dollarAmount).replace(/[^0-9.]/g, '');
    const dollars = parseFloat(cleaned) || 0;
    return Math.round(dollars * 100);
  };

  const formatPriceForInput = (cents) => {
    return cents ? (cents / 100).toFixed(2) : '';
  };

  const toggleCategory = (categoryId) => {
    const currentCategories = formData.categories || [];
    if (currentCategories.includes(categoryId)) {
      setFormData({
        ...formData,
        categories: currentCategories.filter(id => id !== categoryId)
      });
    } else {
      setFormData({
        ...formData,
        categories: [...currentCategories, categoryId]
      });
    }
  };

  const toggleModifierGroup = (groupId) => {
    const currentGroups = formData.modifier_groups || [];
    if (currentGroups.includes(groupId)) {
      setFormData({
        ...formData,
        modifier_groups: currentGroups.filter(id => id !== groupId)
      });
    } else {
      setFormData({
        ...formData,
        modifier_groups: [...currentGroups, groupId]
      });
    }
  };

  const renderMenuItemsTable = () => (
    <>
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th style={{width: '80px'}}>圖片</th>
            <th>中文名稱</th>
            <th>英文名稱</th>
            <th>分類</th>
            <th>價格</th>
            <th>修飾項組</th>
            <th>狀態</th>
            <th style={{textAlign: 'right'}}>操作</th>
          </tr>
        </thead>
        <tbody>
          {filteredItems().map(item => (
            <tr key={item.id}>
              <td>
                {item.image_url ? (
                  <div className="table-image">
                    <img src={item.image_url} alt={item.name} loading="lazy" decoding="async" />
                  </div>
                ) : (
                  <div className="table-image-placeholder">
                    <ImageIcon size={24} />
                  </div>
                )}
              </td>
              <td>
                <div className="table-cell-name">
                  {item.alternate_name || '-'}
                </div>
                {item.printer_name && (
                  <div className="table-cell-subtitle">[列印: {item.printer_name}]</div>
                )}
              </td>
              <td>
                <div className="table-cell-name" style={{color: item.name ? '#1a1a1a' : '#ccc'}}>
                  {item.name || '-'}
                </div>
              </td>
              <td>
                {item.categories && item.categories.length > 0 
                  ? item.categories.map(catId => getCategoryName(catId)).join(', ')
                  : '-'}
              </td>
              <td>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <span className="price-badge">
                    {formatPrice(item.fee_in_cents)}
                  </span>
                  {/* === 修改處：列表顯示 GST/PST 標籤 === */}
                  <div style={{display: 'flex', gap: '2px'}}>
                    {item.tax_gst && (
                      <span style={{
                        fontSize: '0.7rem', 
                        padding: '2px 4px', 
                        background: '#e0f2fe', 
                        color: '#0369a1',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}>GST</span>
                    )}
                    {item.tax_pst && (
                      <span style={{
                        fontSize: '0.7rem', 
                        padding: '2px 4px', 
                        background: '#fce7f3', 
                        color: '#be185d',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}>PST</span>
                    )}
                  </div>
                </div>
              </td>
              <td>
                {item.modifier_groups && item.modifier_groups.length > 0
                  ? `${item.modifier_groups.length} 組`
                  : '-'}
              </td>
              <td>
                <div style={{display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start'}}>
                  <button
                    onClick={() => handleToggleAvailability(item)}
                    className={`availability-toggle ${item.is_available ? 'available' : 'soldout'}`}
                    title={item.is_available ? '點擊設為售罄' : '點擊設為供應中'}
                  >
                    <span className="toggle-slider" />
                    <span className="toggle-text">{item.is_available ? '供應中' : '售罄'}</span>
                  </button>
                  {item.is_hidden && (
                    <span className="status-badge status-hidden">已隱藏</span>
                  )}
                </div>
              </td>
              <td>
                <div className="table-actions">
                  <button 
                    className="btn-icon"
                    onClick={() => handleOpenModal(item)}
                    title="編輯"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button 
                    className="btn-icon"
                    onClick={() => handleDelete(item.id)}
                    title="刪除"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* 手機版卡片列表 */}
    <div className="mobile-cards">
      {filteredItems().map(item => (
        <div className="mobile-card" key={item.id}>
          <div className="mobile-card-header">
            <div className="mobile-card-image">
              {item.image_url ? (
                <img src={item.image_url} alt={item.name} loading="lazy" decoding="async" />
              ) : (
                <ImageIcon size={24} color="#94a3b8" />
              )}
            </div>
            <div className="mobile-card-info">
              <div className="mobile-card-title">{item.alternate_name || item.name || '-'}</div>
              <div className="mobile-card-subtitle">{item.name || '-'}</div>
            </div>
          </div>
          <div className="mobile-card-meta">
            {item.categories && item.categories.length > 0 && (
              <span className="mobile-card-tag tag-category">
                {item.categories.map(catId => getCategoryName(catId)).join(', ')}
              </span>
            )}
            {item.modifier_groups && item.modifier_groups.length > 0 && (
              <span className="mobile-card-tag tag-modifier">{item.modifier_groups.length} 組修飾</span>
            )}
            {item.tax_gst && <span className="mobile-card-tag tag-gst">GST</span>}
            {item.tax_pst && <span className="mobile-card-tag tag-pst">PST</span>}
          </div>
          <div className="mobile-card-footer">
            <div className="mobile-card-price">{formatPrice(item.fee_in_cents)}</div>
            <div className="mobile-card-actions">
              <button
                onClick={() => handleToggleAvailability(item)}
                className={`mobile-availability-toggle ${item.is_available ? 'available' : 'soldout'}`}
                title={item.is_available ? '點擊設為售罄' : '點擊設為供應中'}
              >
                <span className="toggle-track">
                  <span className="toggle-thumb" />
                </span>
                <span className="toggle-label">{item.is_available ? '供應中' : '售罄'}</span>
              </button>
              <button className="mobile-card-edit" onClick={() => handleOpenModal(item)}>
                <Edit2 size={16} /> 編輯
              </button>
              <button className="mobile-card-delete" onClick={() => handleDelete(item.id)}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
    </>
  );

  const renderCategoriesTable = () => (
    <>
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th>分類名稱</th>
            <th>描述</th>
            <th>排序</th>
            <th>狀態</th>
            <th style={{textAlign: 'right'}}>操作</th>
          </tr>
        </thead>
        <tbody>
          {filteredItems().map(category => (
            <tr key={category.id}>
              <td>
                <div className="table-cell-name">
                  {category.alternate_name || category.name}
                </div>
                {category.alternate_name && category.name && (
                  <div className="table-cell-subtitle">{category.name}</div>
                )}
              </td>
              <td>{category.excerpt || '-'}</td>
              <td>{category.order}</td>
              <td>
                <span className={`status-badge ${!category.is_hidden ? 'status-active' : 'status-hidden'}`}>
                  {!category.is_hidden ? '顯示中' : '已隱藏'}
                </span>
              </td>
              <td>
                <div className="table-actions">
                  <button 
                    className="btn-icon"
                    onClick={() => handleOpenModal(category)}
                    title="編輯"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button 
                    className="btn-icon"
                    onClick={() => handleDelete(category.id)}
                    title="刪除"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* 分類手機卡片 */}
    <div className="mobile-cards">
      {filteredItems().map(category => (
        <div className="mobile-card" key={category.id}>
          <div className="mobile-card-header">
            <div className="mobile-card-info" style={{flex: 1}}>
              <div className="mobile-card-title">{category.alternate_name || category.name}</div>
              {category.excerpt && <div className="mobile-card-subtitle">{category.excerpt}</div>}
            </div>
          </div>
          <div className="mobile-card-meta">
            <span className="mobile-card-tag">排序: {category.order}</span>
            <span className={`mobile-card-tag ${!category.is_hidden ? 'tag-active' : 'tag-inactive'}`}>
              {!category.is_hidden ? '顯示中' : '已隱藏'}
            </span>
          </div>
          <div className="mobile-card-footer">
            <div></div>
            <div className="mobile-card-actions">
              <button className="mobile-card-edit" onClick={() => handleOpenModal(category)}>
                <Edit2 size={16} /> 編輯
              </button>
              <button className="mobile-card-delete" onClick={() => handleDelete(category.id)}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
    </>
  );

  const renderModifiersTable = () => (
    <>
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th style={{width: '40px'}}></th>
            <th style={{width: '60px'}}>排序</th>
            <th>修飾項組名稱</th>
            <th>選項數量</th>
            <th>選擇規則</th>
            <th>列印名稱</th>
            <th style={{textAlign: 'right'}}>操作</th>
          </tr>
        </thead>
        <tbody>
          {filteredItems().map(group => (
            <React.Fragment key={group.id}>
              <tr className="expandable-row">
                <td onClick={() => toggleExpandRow(group.id)} style={{cursor: 'pointer'}}>
                  <ChevronRight
                    size={18}
                    className={`expand-icon ${expandedRows.has(group.id) ? 'expanded' : ''}`}
                  />
                </td>
                <td style={{textAlign: 'center'}}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    background: '#f1f5f9',
                    color: '#475569',
                    fontSize: '0.85rem',
                    fontWeight: 600
                  }}>{group.order || '-'}</span>
                </td>
                <td onClick={() => toggleExpandRow(group.id)} style={{cursor: 'pointer'}}>
                  <div className="table-cell-name">
                    {group.alternate_name || group.name}
                  </div>
                  {group.alternate_name && group.name && (
                    <div className="table-cell-subtitle">{group.name}</div>
                  )}
                </td>
                <td>{group.modifiers?.length || 0} 個選項</td>
                <td>
                  <div className="table-cell-subtitle">
                    {group.min_required > 0 ? '必選' : '可選'}
                    {' • '}
                    最少 {group.min_required}，最多 {group.max_allowed}
                  </div>
                </td>
                <td>{group.printer_name || '-'}</td>
                <td>
                  <div className="table-actions">
                    <button 
                      className="btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(group);
                      }}
                      title="編輯"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button 
                      className="btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(group.id);
                      }}
                      title="刪除"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
              {expandedRows.has(group.id) && (
                <tr className="expanded-content-row">
                  <td colSpan="7">
                    <div className="expanded-content">
                      <h4 style={{fontSize: '0.9rem', fontWeight: 600, color: '#475569', marginBottom: '12px'}}>
                        包含的修飾項選項：
                      </h4>
                      {group.modifiers && group.modifiers.length > 0 ? (
                        <div className="modifier-options-list">
                          {[...group.modifiers].sort((a, b) => (a.order || 0) - (b.order || 0)).map(modifier => (
                            <div key={modifier.id} className="modifier-option-card">
                              <div className="modifier-option-info">
                                <div className="modifier-option-name-text">
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    background: '#e2e8f0',
                                    color: '#64748b',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    marginRight: '8px'
                                  }}>{modifier.order || '-'}</span>
                                  {modifier.alternate_name || modifier.name}
                                </div>
                                {modifier.alternate_name && modifier.name && (
                                  <div className="modifier-option-alternate">
                                    {modifier.name}
                                  </div>
                                )}
                                {modifier.printer_name && (
                                  <div className="modifier-option-alternate">
                                    列印名: {modifier.printer_name}
                                  </div>
                                )}
                              </div>
                              <div style={{display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end'}}>
                                {modifier.fee_in_cents !== 0 && (
                                  <span className="price-badge" style={{
                                    background: modifier.fee_in_cents > 0 ? '#dcfce7' : '#fee2e2',
                                    color: modifier.fee_in_cents > 0 ? '#166534' : '#991b1b'
                                  }}>
                                    {modifier.fee_in_cents > 0 ? '+' : ''}{formatPrice(modifier.fee_in_cents)}
                                  </span>
                                )}
                                {modifier.is_complimentary && (
                                  <span className="status-badge" style={{background: '#fef3c7', color: '#92400e', fontSize: '0.75rem'}}>
                                    免費
                                  </span>
                                )}
                                {!modifier.is_available && (
                                  <span className="status-badge status-inactive" style={{fontSize: '0.75rem'}}>缺貨</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{color: '#64748b', fontSize: '0.9rem'}}>此修飾項組尚未包含任何選項</p>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>

    {/* 修飾項組手機卡片 */}
    <div className="mobile-cards">
      {filteredItems().map(group => (
        <div className="mobile-card" key={group.id}>
          <div className="mobile-card-header">
            <div className="mobile-card-info" style={{flex: 1}}>
              <div className="mobile-card-title">{group.alternate_name || group.name}</div>
              {group.printer_name && <div className="mobile-card-subtitle">列印: {group.printer_name}</div>}
            </div>
          </div>
          <div className="mobile-card-meta">
            <span className="mobile-card-tag">排序: {group.order || '-'}</span>
            <span className="mobile-card-tag tag-modifier">{group.modifiers?.length || 0} 個選項</span>
            <span className="mobile-card-tag">
              {group.min_required > 0 ? `必選${group.min_required}` : '可選'} / 最多{group.max_allowed}
            </span>
          </div>
          <div className="mobile-card-footer">
            <div></div>
            <div className="mobile-card-actions">
              <button className="mobile-card-edit" onClick={() => handleOpenModal(group)}>
                <Edit2 size={16} /> 編輯
              </button>
              <button className="mobile-card-delete" onClick={() => handleDelete(group.id)}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
    </>
  );

  const renderModalForm = () => {
    if (activeTab === 'categories') {
       return (
        <>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label required">中文名稱</label>
              <input 
                type="text"
                className="form-input"
                value={formData.alternate_name || ''}
                onChange={(e) => setFormData({...formData, alternate_name: e.target.value})}
                placeholder="漢堡"
              />
            </div>
            <div className="form-group">
              <label className="form-label">英文名稱</label>
              <input 
                type="text"
                className="form-input"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Burgers"
              />
            </div>
          </div>
  
          <div className="form-group full-width">
            <label className="form-label">簡短描述</label>
            <input 
              type="text" 
              className="form-input" 
              value={formData.excerpt || ''} 
              onChange={(e) => setFormData({...formData, excerpt: e.target.value})} 
            />
          </div>
  
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">排序權重</label>
              <input 
                type="number" 
                className="form-input" 
                value={formData.order || 1} 
                onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})} 
              />
            </div>
          </div>
  
          <div className="form-row">
            <div className="form-group">
              <label className="form-checkbox">
                <input 
                  type="checkbox" 
                  checked={formData.is_hidden || false} 
                  onChange={(e) => setFormData({...formData, is_hidden: e.target.checked})} 
                />
                <span>隱藏此分類</span>
              </label>
            </div>
          </div>
        </>
      );
    }
    
    if (activeTab === 'items') {
      const selectedModifierGroups = modifierGroups.filter(g => 
        (formData.modifier_groups || []).includes(g.id)
      );
      
      const availableModifierGroups = modifierGroups.filter(g => {
        const isNotSelected = !(formData.modifier_groups || []).includes(g.id);
        const matchesSearch = modifierSearchTerm === '' || 
          (g.name && g.name.toLowerCase().includes(modifierSearchTerm.toLowerCase())) || 
          (g.alternate_name && g.alternate_name.toLowerCase().includes(modifierSearchTerm.toLowerCase()));
        return isNotSelected && matchesSearch;
      });
  
      return (
        <>
          <div className="form-section">
            <div className="form-section-title">
              <Utensils size={20} />
              基本資訊
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">中文名稱</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.alternate_name || ''}
                  onChange={(e) => setFormData({...formData, alternate_name: e.target.value})}
                  placeholder="經典牛肉漢堡"
                />
              </div>
              <div className="form-group">
                <label className="form-label">英文名稱</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Classic Beef Burger"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">列印名稱</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.printer_name || ''}
                  onChange={(e) => setFormData({...formData, printer_name: e.target.value})}
                  placeholder="BEEF BURGER"
                />
              </div>
            </div>
  
            <div className="form-group full-width">
              <label className="form-label">URL別名</label>
              <input 
                type="text"
                className="form-input"
                value={formData.slug || ''}
                onChange={(e) => setFormData({...formData, slug: e.target.value})}
                placeholder="classic-beef-burger"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">標準價格</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-input"
                  defaultValue={formatPriceForInput(formData.fee_in_cents)}
                  key={`price-${editingItem?.id || 'new'}`}
                  onBlur={(e) => setFormData(prev => ({...prev, fee_in_cents: parsePriceToCents(e.target.value)}))}
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label className="form-label">適用稅率 (Taxes)</label>
                <div style={{display: 'flex', gap: '20px', paddingTop: '10px'}}>
                  <label className="form-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.tax_gst !== false}
                      onChange={(e) => setFormData({...formData, tax_gst: e.target.checked})}
                    />
                    <span>GST (5%)</span>
                  </label>
                  <label className="form-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.tax_pst !== false}
                      onChange={(e) => setFormData({...formData, tax_pst: e.target.checked})}
                    />
                    <span>PST (7%)</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="form-group full-width">
              <label className="form-label">中文描述</label>
              <textarea
                className="form-textarea"
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
            <div className="form-group full-width">
              <label className="form-label">英文描述</label>
              <textarea
                className="form-textarea"
                value={formData.description_en || ''}
                onChange={(e) => setFormData({...formData, description_en: e.target.value})}
              />
            </div>
          </div>
          
          <div className="form-section" style={{marginTop: '20px'}}>
            <div className="form-section-title">
              <ImageIcon size={20} />
              圖片設定
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label className="form-label">圖片</label>
                <div className="image-upload-container">
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display: 'none' }}
                  />

                  {/* Clickable preview area */}
                  <div
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    style={{
                      width: '120px',
                      height: '120px',
                      background: '#f8fafc',
                      borderRadius: '12px',
                      border: '2px dashed #cbd5e1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      cursor: isUploading ? 'wait' : 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => { if (!isUploading) e.currentTarget.style.borderColor = '#3b82f6'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; }}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
                        <span style={{fontSize: '0.75rem', color: '#64748b', marginTop: '8px'}}>上傳中...</span>
                      </>
                    ) : formData.image_url ? (
                      <>
                        <img
                          src={formData.image_url}
                          alt="預覽"
                          style={{width: '100%', height: '100%', objectFit: 'cover'}}
                          onError={(e) => {e.target.style.display='none';}}
                        />
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          fontSize: '0.7rem',
                          padding: '4px',
                          textAlign: 'center'
                        }}>
                          點擊更換
                        </div>
                      </>
                    ) : (
                      <>
                        <Upload size={24} style={{ color: '#94a3b8' }} />
                        <span style={{fontSize: '0.75rem', color: '#94a3b8', marginTop: '8px'}}>點擊上傳</span>
                      </>
                    )}
                  </div>

                  {/* URL input and upload button */}
                  <div className="image-upload-right">
                    <div className="image-upload-input-row">
                      <input
                        type="text"
                        className="form-input"
                        value={formData.image_url || ''}
                        onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                        placeholder="或貼上圖片網址..."
                        style={{flex: 1}}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        style={{
                          padding: '8px 16px',
                          background: isUploading ? '#e2e8f0' : '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: isUploading ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {isUploading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={16} />}
                        {isUploading ? '上傳中' : '上傳圖片'}
                      </button>
                    </div>
                    <div className="form-hint" style={{marginTop: '6px'}}>
                      支援 JPG, PNG, WebP, GIF (最大 5MB)
                    </div>
                    {formData.image_url && (
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, image_url: ''})}
                        style={{
                          marginTop: '8px',
                          padding: '4px 10px',
                          background: '#fef2f2',
                          color: '#dc2626',
                          border: '1px solid #fecaca',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        移除圖片
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <div className="form-section">
            <div className="form-section-title">
              <Tag size={20} />
              分類與修飾項
            </div>
            
            <div className="form-group full-width">
              <label className="form-label">所屬分類</label>
              <div className="multi-select-tags">
                {categories.map(cat => (
                  <div
                    key={cat.id}
                    className={`selectable-tag ${(formData.categories || []).includes(cat.id) ? 'selected' : ''}`}
                    onClick={() => toggleCategory(cat.id)}
                  >
                    {cat.alternate_name || cat.name}
                  </div>
                ))}
              </div>
            </div>
  
            <div className="form-group full-width">
              <label className="form-label">修飾項組 (Add-ons & Options)</label>
              <div className="modifier-selector-container">
                
                <div className="selected-modifiers-area">
                  <div className="section-label">已選擇 ({selectedModifierGroups.length})</div>
                  {selectedModifierGroups.length > 0 ? (
                    <div className="selected-modifier-chips">
                      {selectedModifierGroups.map(group => (
                        <div key={group.id} className="modifier-chip">
                          <span>{group.alternate_name || group.name}</span>
                          <button 
                            type="button" 
                            onClick={() => toggleModifierGroup(group.id)}
                            title="移除"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic'}}>
                      尚未選擇任何修飾項組
                    </div>
                  )}
                </div>
  
                <div className="available-modifiers-area">
                  <div className="section-label">新增修飾項</div>
                  <input 
                    type="text" 
                    className="modifier-search-input"
                    placeholder="搜尋修飾項組名稱..."
                    value={modifierSearchTerm}
                    onChange={(e) => setModifierSearchTerm(e.target.value)}
                  />
                  
                  <div className="available-list">
                    {availableModifierGroups.map(group => (
                      <div 
                        key={group.id} 
                        className="available-item"
                        onClick={() => toggleModifierGroup(group.id)}
                      >
                        <div className="available-item-name">
                          {group.alternate_name || group.name}
                        </div>
                        <Plus size={16} color="#64748b" />
                      </div>
                    ))}
                    {availableModifierGroups.length === 0 && (
                      <div style={{gridColumn: '1/-1', textAlign: 'center', color: '#94a3b8', padding: '10px'}}>
                        {modifierSearchTerm ? '找不到符合的修飾項組' : '沒有更多可選的修飾項組'}
                      </div>
                    )}
                  </div>
                </div>
  
              </div>
            </div>
          </div>
        </>
      );
    }
  
    if (activeTab === 'modifiers') {
      return (
        <>
          <div className="form-section">
            <div className="form-section-title">
              <Settings size={20} />
              修飾項組資訊
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">中文名稱</label>
                <input 
                  type="text"
                  className="form-input"
                  value={formData.alternate_name || ''}
                  onChange={(e) => setFormData({...formData, alternate_name: e.target.value})}
                  placeholder="加飲品"
                />
              </div>
              <div className="form-group">
                <label className="form-label">英文名稱</label>
                <input 
                  type="text"
                  className="form-input"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Add a Drink"
                />
              </div>
            </div>
  
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">列印名稱</label>
                <input 
                  type="text"
                  className="form-input"
                  value={formData.printer_name || ''}
                  onChange={(e) => setFormData({...formData, printer_name: e.target.value})}
                  placeholder="DRINK"
                />
              </div>
            </div>
  
            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">最少選擇數量</label>
                <input 
                  type="number"
                  className="form-input"
                  value={formData.min_required || 0}
                  onChange={(e) => setFormData({...formData, min_required: parseInt(e.target.value)})}
                  min="0"
                />
                <div className="form-hint">0 = 可選，1+ = 必選</div>
              </div>
              <div className="form-group">
                <label className="form-label required">最多選擇數量</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.max_allowed || 1}
                  onChange={(e) => setFormData({...formData, max_allowed: parseInt(e.target.value)})}
                  min="1"
                />
                <div className="form-hint">1 = 單選，2+ = 多選</div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">顯示順序</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.order || 1}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 1})}
                  min="1"
                />
                <div className="form-hint">數字越小越前面顯示</div>
              </div>
            </div>
          </div>
  
          <div className="form-section">
            <div className="form-section-title">
              <Utensils size={20} />
              修飾項選項
              <button 
                type="button"
                className="btn-secondary btn-small"
                style={{marginLeft: 'auto'}}
                onClick={() => {
                  const currentModifiers = formData.modifiers || [];
                  const maxOrder = currentModifiers.length > 0
                    ? Math.max(...currentModifiers.map(m => m.order || 0))
                    : 0;
                  const newModifier = {
                    id: Date.now(),
                    alternate_name: '',
                    name: '',
                    printer_name: '',
                    fee_in_cents: 0,
                    is_available: true,
                    is_complimentary: false,
                    order: maxOrder + 1
                  };
                  setFormData({
                    ...formData,
                    modifiers: [newModifier, ...(formData.modifiers || [])]
                  });
                }}
              >
                <Plus size={14} />
                新增選項
              </button>
            </div>
  
            {formData.modifiers && formData.modifiers.length > 0 ? (
              formData.modifiers.map((modifier, index) => (
                <div key={modifier.id || index} className="modifier-group-card">
                  <div className="modifier-group-header">
                    <div className="modifier-group-title">選項 {index + 1}</div>
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => {
                        const newModifiers = formData.modifiers.filter((_, i) => i !== index);
                        setFormData({...formData, modifiers: newModifiers});
                      }}
                      title="刪除此選項"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">中文名稱</label>
                      <input 
                        type="text"
                        className="form-input"
                        value={modifier.alternate_name || ''}
                        onChange={(e) => {
                          const newModifiers = [...formData.modifiers];
                          newModifiers[index] = {...modifier, alternate_name: e.target.value};
                          setFormData({...formData, modifiers: newModifiers});
                        }}
                        placeholder="可樂"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">英文名稱</label>
                      <input 
                        type="text"
                        className="form-input"
                        value={modifier.name || ''}
                        onChange={(e) => {
                          const newModifiers = [...formData.modifiers];
                          newModifiers[index] = {...modifier, name: e.target.value};
                          setFormData({...formData, modifiers: newModifiers});
                        }}
                        placeholder="Coke"
                      />
                    </div>
                  </div>
  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">列印名稱</label>
                      <input
                        type="text"
                        className="form-input"
                        value={modifier.printer_name || ''}
                        onChange={(e) => {
                          const newModifiers = [...formData.modifiers];
                          newModifiers[index] = {...modifier, printer_name: e.target.value};
                          setFormData({...formData, modifiers: newModifiers});
                        }}
                        placeholder="COKE"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">顯示順序</label>
                      <input
                        type="number"
                        className="form-input"
                        value={modifier.order || index + 1}
                        onChange={(e) => {
                          const newModifiers = [...formData.modifiers];
                          newModifiers[index] = {...modifier, order: parseInt(e.target.value) || 1};
                          setFormData({...formData, modifiers: newModifiers});
                        }}
                        min="1"
                        placeholder="1"
                      />
                      <div className="form-hint">數字越小越前面</div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">價格調整</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-input"
                        defaultValue={formatPriceForInput(modifier.fee_in_cents)}
                        key={`mod-price-${modifier.id || index}`}
                        onBlur={(e) => {
                          const newModifiers = [...formData.modifiers];
                          newModifiers[index] = {...modifier, fee_in_cents: parsePriceToCents(e.target.value)};
                          setFormData({...formData, modifiers: newModifiers});
                        }}
                        placeholder="2.50"
                      />
                      <div className="form-hint">正數加價，負數減價，0 不調整</div>
                    </div>
                  </div>
  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-checkbox">
                        <input 
                          type="checkbox"
                          checked={modifier.is_available !== false}
                          onChange={(e) => {
                            const newModifiers = [...formData.modifiers];
                            newModifiers[index] = {...modifier, is_available: e.target.checked};
                            setFormData({...formData, modifiers: newModifiers});
                          }}
                        />
                        <span>目前可供應</span>
                      </label>
                    </div>
                    <div className="form-group">
                      <label className="form-checkbox">
                        <input 
                          type="checkbox"
                          checked={modifier.is_complimentary || false}
                          onChange={(e) => {
                            const newModifiers = [...formData.modifiers];
                            newModifiers[index] = {...modifier, is_complimentary: e.target.checked};
                            setFormData({...formData, modifiers: newModifiers});
                          }}
                        />
                        <span>免費贈送</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{textAlign: 'center', padding: '20px', color: '#64748b'}}>
                <p>尚未新增任何選項，點擊上方「新增選項」按鈕開始</p>
              </div>
            )}
          </div>
        </>
      );
    }
  
    return null;
  };

  return (
    <>
      <AdminMenuStyles />
      {importProgress && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '32px 48px',
            textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <Loader2 size={36} style={{ animation: 'spin 1s linear infinite', color: '#2F7D5E', marginBottom: '16px' }} />
            <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
              匯入中...
            </div>
            <div style={{ fontSize: '0.95rem', color: '#64748b' }}>
              正在處理 {importProgress.current} / {importProgress.total} 筆
            </div>
            <div style={{
              marginTop: '16px', background: '#f1f5f9', borderRadius: '8px',
              height: '8px', overflow: 'hidden'
            }}>
              <div style={{
                height: '100%', background: '#2F7D5E', borderRadius: '8px',
                width: `${(importProgress.current / importProgress.total) * 100}%`,
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        </div>
      )}
      <div className="admin-menu-container">

        <div className="menu-tabs">
          <button 
            className={`menu-tab ${activeTab === 'items' ? 'active' : ''}`}
            onClick={() => setActiveTab('items')}
          >
            <Utensils size={18} />
            菜式管理
          </button>
          <button 
            className={`menu-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            <Tag size={18} />
            分類管理
          </button>
          <button 
            className={`menu-tab ${activeTab === 'modifiers' ? 'active' : ''}`}
            onClick={() => setActiveTab('modifiers')}
          >
            <Settings size={18} />
            修飾項組管理
          </button>
        </div>

        <div className="menu-toolbar">
          <div className="toolbar-left">
            <div className="search-box">
              <Search size={18} />
              <input 
                type="text" 
                placeholder={`搜尋${activeTab === 'categories' ? '分類' : activeTab === 'items' ? '菜式' : '修飾項'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {activeTab === 'items' && (
              <div className="filter-box">
                <Filter size={18} color="#64748b" />
                <select 
                  className="filter-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">所有分類</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.alternate_name || cat.name}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} color="#64748b" style={{position: 'absolute', right: '10px', pointerEvents: 'none'}} />
              </div>
            )}
          </div>

          <div className="action-buttons">
            <button className="btn-secondary" onClick={handleExport} title="匯出成 Excel">
              <Download size={18} />
              <span className="hidden-mobile">匯出</span>
            </button>
            
            {activeTab === 'items' && (
              <label className="btn-secondary" style={{cursor: 'pointer'}} title="匯入 Excel">
                <Upload size={18} />
                <span className="hidden-mobile">匯入</span>
                <input 
                  type="file" 
                  className="file-input"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
                  onChange={handleImport}
                />
              </label>
            )}

            <button className="btn-primary" onClick={() => handleOpenModal()}>
              <Plus size={18} />
              新增{activeTab === 'categories' ? '分類' : 
                   activeTab === 'items' ? '菜式' : '修飾項組'}
            </button>
          </div>
        </div>

        {filteredItems().length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              {activeTab === 'categories' ? <Tag size={80} /> :
               activeTab === 'items' ? <Utensils size={80} /> :
               <Settings size={80} />}
            </div>
            <h3>尚無資料</h3>
            <p>
              {categoryFilter !== 'all' 
                ? '此分類下沒有找到符合的菜式' 
                : '點擊上方「新增」按鈕開始建立，或使用「匯入」功能'}
            </p>
          </div>
        ) : (
          <>
            {activeTab === 'items' && renderMenuItemsTable()}
            {activeTab === 'categories' && renderCategoriesTable()}
            {activeTab === 'modifiers' && renderModifiersTable()}
          </>
        )}

        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  {editingItem ? <Edit2 size={24} /> : <Plus size={24} />}
                  {editingItem ? '編輯' : '新增'}
                  {activeTab === 'categories' ? '分類' : 
                   activeTab === 'items' ? '菜式' : '修飾項組'}
                </h2>
                <button className="btn-icon" onClick={handleCloseModal}>
                  <X size={24} />
                </button>
              </div>

              <div className="modal-body">
                {renderModalForm()}
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={handleCloseModal}>
                  取消
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSave}
                  disabled={loading || (activeTab === 'items' ? !formData.name : (!formData.alternate_name && !formData.name))}
                >
                  <Save size={18} />
                  {loading ? '儲存中...' : '儲存'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        @media (max-width: 600px) {
          .hidden-mobile {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default AdminMenu;